export type SchemaColumn = {
  name: string;
  type: string;
  kind: 'column' | 'partition';
};

export type SchemaTable = {
  id: string;
  database: string;
  table: string;
  sourceFamily: string;
  description: string;
  location: string;
  tableType: string;
  classification: string;
  columnCount: number;
  partitionCount: number;
  columns: SchemaColumn[];
};

export type SchemaCatalogResponse = {
  live: boolean;
  source: string;
  generatedAt: string;
  summary: {
    databaseCount: number;
    tableCount: number;
    columnCount: number;
    countsByDatabase: Record<string, { tables: number; columns: number }>;
    countsBySourceFamily: Record<string, number>;
  };
  filters: {
    databases: string[];
    sourceFamilies: string[];
  };
  tables: SchemaTable[];
  query?: {
    q?: string;
    database?: string;
    sourceFamily?: string;
    resultCount?: number;
  };
  matches?: Array<{
    tableId: string;
    score: number;
    matchedTerms: string[];
    topColumns: SchemaColumn[];
  }>;
};

export type SchemaAskResponse = {
  answer: string;
  confidence: string;
  recommendedTables: string[];
  recommendedColumns: Array<{
    tableId: string;
    column: string;
    type: string;
    kind: string;
    score?: number;
  }>;
  caveats: string[];
  matchedTables: Array<{
    id: string;
    database: string;
    table: string;
    sourceFamily: string;
    columnCount: number;
    partitionCount: number;
    score: number;
  }>;
  matchedColumns: Array<{
    tableId: string;
    column: string;
    type: string;
    kind: string;
    score?: number;
  }>;
  catalogGeneratedAt: string;
  requestedAt: string;
  model: string;
};

type SchemaAskRequest = {
  question: string;
  database?: string;
  sourceFamily?: string;
};

const SCHEMA_CATALOG_ENDPOINT = '/analytics/brain-dashboard/v1/schema/catalog';
const SCHEMA_ASK_ENDPOINT = '/analytics/brain-dashboard/v1/schema/ask';

const ensureApiBaseUrl = (apiBaseUrl: string): string => {
  const trimmed = String(apiBaseUrl || '').trim().replace(/\/+$/, '');
  if (!trimmed) {
    throw new Error('API base URL is not configured.');
  }
  return trimmed;
};

const parseJson = async <T>(response: Response): Promise<T> => {
  const text = await response.text();
  return text ? JSON.parse(text) as T : ({} as T);
};

export const fetchSchemaCatalog = async (
  apiBaseUrl: string,
  query?: { q?: string; database?: string; sourceFamily?: string; refresh?: boolean },
): Promise<SchemaCatalogResponse> => {
  const baseUrl = ensureApiBaseUrl(apiBaseUrl);
  const params = new URLSearchParams();
  if (query?.q) params.set('q', query.q);
  if (query?.database) params.set('database', query.database);
  if (query?.sourceFamily) params.set('sourceFamily', query.sourceFamily);
  if (query?.refresh) params.set('refresh', 'true');
  const response = await fetch(
    `${baseUrl}${SCHEMA_CATALOG_ENDPOINT}${params.toString() ? `?${params}` : ''}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    },
  );
  if (!response.ok) {
    const payload = await parseJson<{ error?: string }>(response).catch(() => ({ error: '' }));
    throw new Error(payload.error || `Failed to load schema catalog (${response.status})`);
  }
  return parseJson<SchemaCatalogResponse>(response);
};

export const askSchemaExplorer = async (
  apiBaseUrl: string,
  payload: SchemaAskRequest,
): Promise<SchemaAskResponse> => {
  const baseUrl = ensureApiBaseUrl(apiBaseUrl);
  const response = await fetch(`${baseUrl}${SCHEMA_ASK_ENDPOINT}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const body = await parseJson<{ error?: string }>(response).catch(() => ({ error: '' }));
    throw new Error(body.error || `Failed to ask schema explorer (${response.status})`);
  }
  return parseJson<SchemaAskResponse>(response);
};
